import { InputV2 } from '@southfields-digital/mpxlive-components';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { isValidDimension, MAX_DIMENSION } from 'src/utils/resolution';

const CUSTOM_VALUE = '__custom__';

const toPresetKey = (o: { width: number; height: number }) => `${o.width}x${o.height}`;

const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
  e.stopPropagation();
  if (e.key === 'Enter') e.currentTarget.blur();
};

type Option = { label: string; width: number; height: number };

type Props = {
  selectedPreset: Option | null;
  presets: Option[];
  resolution: { width: number; height: number };
  onChange: (width: number, height: number) => void;
};

const getPresetSelectValue = (preset: Option | null, presets: Option[]) => {
  if (!preset) return CUSTOM_VALUE;
  const key = toPresetKey(preset);
  return presets.some((p) => toPresetKey(p) === key) ? key : CUSTOM_VALUE;
};

const ResolutionPicker = ({ selectedPreset, presets, resolution, onChange }: Props) => {
  const { width, height } = resolution;
  const [selectedValue, setSelectedValue] = useState(() =>
    getPresetSelectValue(selectedPreset, presets)
  );
  const [widthInput, setWidthInput] = useState(width.toString());
  const [heightInput, setHeightInput] = useState(height.toString());

  useEffect(() => {
    setSelectedValue(getPresetSelectValue(selectedPreset, presets));
  }, [selectedPreset, presets]);

  useEffect(() => {
    setWidthInput(width.toString());
    setHeightInput(height.toString());
  }, [width, height]);

  const handleSelectChange = (value: string) => {
    if (value === CUSTOM_VALUE) {
      setSelectedValue(CUSTOM_VALUE);
      return;
    }
    const option = presets.find((o) => toPresetKey(o) === value);
    if (option) {
      setSelectedValue(value);
      onChange(option.width, option.height);
    }
  };

  const handleWidthBlur = () => {
    const next = Math.round(Number(widthInput));
    if (isValidDimension(next) && next !== width) onChange(next, height);
    else setWidthInput(width.toString());
  };

  const handleHeightBlur = () => {
    const next = Math.round(Number(heightInput));
    if (isValidDimension(next) && next !== height) onChange(width, next);
    else setHeightInput(height.toString());
  };

  const selectOptions = [
    ...presets.map((o) => ({ value: toPresetKey(o), label: o.label })),
    { value: CUSTOM_VALUE, label: 'Custom' },
  ];

  return (
    <div className="flex items-center gap-1 min-w-0">
      <InputV2
        type="select"
        value={selectedValue}
        options={selectOptions}
        wrapperClassName={classNames(
          'flex-1 min-w-0',
          // TODO: REMOVE THIS STYLING OVERRIDE WHEN ALL INPUTS USE INPUTV2
          '!min-h-[42px] !rounded-[4px] !bg-elevated-0 !border-gray-800'
        )}
        onChange={(e) => handleSelectChange(e.currentTarget.value)}
      />
      {selectedValue === CUSTOM_VALUE && (
        <>
          <InputV2
            type="number"
            min={1}
            max={MAX_DIMENSION}
            value={widthInput}
            prefix="W"
            wrapperClassName="flex-1 min-w-0"
            onChange={(e) => setWidthInput(e.currentTarget.value)}
            onKeyDown={keyDownHandler}
            onBlur={handleWidthBlur}
          />
          <InputV2
            type="number"
            min={1}
            max={MAX_DIMENSION}
            value={heightInput}
            prefix="H"
            wrapperClassName="flex-1 min-w-0"
            onChange={(e) => setHeightInput(e.currentTarget.value)}
            onKeyDown={keyDownHandler}
            onBlur={handleHeightBlur}
          />
        </>
      )}
    </div>
  );
};

export default ResolutionPicker;
